"use client";
import { useCallback, useEffect, useState } from "react";

const SEEN_FEATURES_KEY = "tactic_seen_features";

export interface SeenFeature {
  id: string;
  seenAt: string;
}

export interface SeenFeaturesStorage {
  features: SeenFeature[];
}

const getStoredSeenFeatures = (): SeenFeaturesStorage => {
  if (typeof window === "undefined") {
    return { features: [] };
  }

  try {
    const stored = localStorage.getItem(SEEN_FEATURES_KEY);
    if (stored) {
      return JSON.parse(stored) as SeenFeaturesStorage;
    }
  } catch {
    // Invalid JSON, reset storage
  }

  return { features: [] };
};

const saveSeenFeatures = (storage: SeenFeaturesStorage): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(SEEN_FEATURES_KEY, JSON.stringify(storage));
  } catch {
    // localStorage may throw QuotaExceededError or SecurityError in private browsing
  }
};

export const useSeenFeatures = () => {
  const [seenFeatures, setSeenFeatures] = useState<SeenFeature[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const storage = getStoredSeenFeatures();
    setSeenFeatures(storage.features);
    setIsLoaded(true);
  }, []);

  const markFeatureAsSeen = useCallback((featureId: string) => {
    const now = new Date().toISOString();
    const newSeenFeature: SeenFeature = {
      id: featureId,
      seenAt: now
    };

    setSeenFeatures((prev) => {
      // Don't add if already seen
      if (prev.some((f) => f.id === featureId)) {
        return prev;
      }

      const updated = [...prev, newSeenFeature];
      saveSeenFeatures({ features: updated });
      return updated;
    });
  }, []);

  const isFeatureSeen = useCallback(
    (featureId: string): boolean => {
      return seenFeatures.some((f) => f.id === featureId);
    },
    [seenFeatures]
  );

  const getSeenFeatureIds = useCallback((): string[] => {
    return seenFeatures.map((f) => f.id);
  }, [seenFeatures]);

  const clearSeenFeatures = useCallback(() => {
    setSeenFeatures([]);
    saveSeenFeatures({ features: [] });
  }, []);

  return {
    seenFeatures,
    isLoaded,
    markFeatureAsSeen,
    isFeatureSeen,
    getSeenFeatureIds,
    clearSeenFeatures
  };
};
